<template>
  <div class="row">
    <div class="col-xl-12 order-xl-1">
      <card card-body-classes="table-full-width" no-footer-line>
        <div slot="header" class="user-list-header">
          <div class="user-list-heading">
            <h4 class="card-title mb-0">Liste des comptes utilisateurs</h4>
            <span
              class="user-count"
              aria-live="polite"
              :aria-label="`${meta.total} compte${meta.total === 1 ? '' : 's'}`"
            >
              {{ meta.total }}
            </span>
          </div>

          <div class="user-list-header-actions">
            <button
              type="button"
              class="btn user-export-action"
              :disabled="isExporting || isLoading || meta.total === 0"
              :aria-label="`Exporter ${meta.total} compte${meta.total === 1 ? '' : 's'} au format Excel`"
              :title="`Exporter les ${meta.total} compte${meta.total === 1 ? '' : 's'} correspondant aux filtres dans un fichier Excel mis en forme`"
              @click="exportUsers"
            >
              <span class="user-export-action-icon" aria-hidden="true">
                <i :class="isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              </span>
              <span>{{ isExporting ? 'Préparation…' : 'Exporter' }}</span>
              <span v-if="!isExporting" class="user-export-format" aria-hidden="true">XLSX</span>
            </button>

            <button type="button" class="btn user-primary-action" @click="addUser">
              <i class="fas fa-plus mr-2" aria-hidden="true"></i>
              Ajouter un compte
            </button>
          </div>
        </div>

        <div class="user-list-content">
          <form
            class="user-filters"
            :class="{ 'user-filters--with-actions': hasFilters }"
            role="search"
            @submit.prevent="applyFilters"
          >
            <div class="user-filter user-filter--search">
              <label for="user-search">Rechercher</label>
              <div class="user-search-input">
                <i class="fas fa-search" aria-hidden="true"></i>
                <input
                  id="user-search"
                  v-model="search"
                  type="search"
                  class="form-control"
                  autocomplete="off"
                  placeholder="Nom ou adresse e-mail"
                  @input="onSearchInput"
                />
              </div>
            </div>

            <div class="user-filter">
              <label for="user-role-filter">Profil</label>
              <el-select
                id="user-role-filter"
                v-model="role"
                class="user-filter-select"
                aria-label="Filtrer par profil"
                placeholder="Tous les profils"
                popper-class="user-filter-popper"
                @change="applyFilters"
              >
                <el-option label="Tous les profils" value=""></el-option>
                <el-option
                  v-for="roleName in roles"
                  :key="roleName"
                  :label="roleName"
                  :value="roleName"
                ></el-option>
              </el-select>
            </div>

            <div class="user-filter">
              <label for="user-status-filter">Statut</label>
              <el-select
                id="user-status-filter"
                v-model="status"
                class="user-filter-select"
                aria-label="Filtrer par statut"
                placeholder="Actifs et désactivés"
                popper-class="user-filter-popper"
                @change="applyFilters"
              >
                <el-option label="Actifs et désactivés" value=""></el-option>
                <el-option label="Actifs" value="active"></el-option>
                <el-option label="Désactivés" value="inactive"></el-option>
                <el-option label="Supprimés" value="deleted"></el-option>
              </el-select>
            </div>

            <div v-if="hasFilters" class="user-filter-actions">
              <button
                type="button"
                class="btn user-secondary-action"
                @click="clearFilters"
              >
                <i class="fas fa-times mr-2" aria-hidden="true"></i>
                Effacer
              </button>
            </div>
          </form>

          <div v-if="loadError && !isLoading" class="user-state user-state--error" role="alert">
            <span class="user-state-icon" aria-hidden="true">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
            <h5>Impossible de charger les comptes</h5>
            <p>{{ loadError }}</p>
            <button type="button" class="btn user-primary-action" @click="loadUsers">
              <i class="fas fa-redo mr-2" aria-hidden="true"></i>
              Réessayer
            </button>
          </div>

          <div
            v-else-if="!isLoading && users.length === 0"
            class="user-state"
            role="status"
          >
            <span class="user-state-icon" aria-hidden="true">
              <i :class="hasFilters ? 'fas fa-search' : 'fas fa-users'"></i>
            </span>
            <template v-if="hasFilters">
              <h5>Aucun résultat</h5>
              <p>Aucun compte ne correspond à votre recherche ou à vos filtres.</p>
              <button type="button" class="btn user-secondary-action" @click="clearFilters">
                Effacer les filtres
              </button>
            </template>
            <template v-else>
              <h5>Aucun compte</h5>
              <p>Créez le premier compte utilisateur pour commencer.</p>
              <button type="button" class="btn user-primary-action" @click="addUser">
                <i class="fas fa-plus mr-2" aria-hidden="true"></i>
                Ajouter un compte
              </button>
            </template>
          </div>

          <div v-else class="table-responsive user-table-wrapper">
            <table class="table user-table mb-0">
              <caption class="sr-only">Liste paginée des comptes utilisateurs</caption>
              <colgroup>
                <col class="user-column-name" />
                <col class="user-column-email" />
                <col class="user-column-role" />
                <col class="user-column-spinner" />
                <col class="user-column-status" />
                <col class="user-column-last-login" />
                <col class="user-column-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" :aria-sort="ariaSort('name')">
                    <button type="button" class="sort-button" @click="changeSort('name')">
                      Nom
                      <i :class="sortIcon('name')" aria-hidden="true"></i>
                    </button>
                  </th>
                  <th scope="col" class="user-column-email" :aria-sort="ariaSort('email')">
                    <button type="button" class="sort-button" @click="changeSort('email')">
                      E-mail
                      <i :class="sortIcon('email')" aria-hidden="true"></i>
                    </button>
                  </th>
                  <th scope="col" class="user-column-role" :aria-sort="ariaSort('role')">
                    <button type="button" class="sort-button" @click="changeSort('role')">
                      Profil
                      <i :class="sortIcon('role')" aria-hidden="true"></i>
                    </button>
                  </th>
                  <th scope="col" class="user-column-spinner" :aria-sort="ariaSort('spinner')">
                    <button type="button" class="sort-button" @click="changeSort('spinner')">
                      Spinner
                      <i :class="sortIcon('spinner')" aria-hidden="true"></i>
                    </button>
                  </th>
                  <th scope="col" :aria-sort="ariaSort('is_active')">
                    <button type="button" class="sort-button" @click="changeSort('is_active')">
                      Statut
                      <i :class="sortIcon('is_active')" aria-hidden="true"></i>
                    </button>
                  </th>
                  <th
                    scope="col"
                    class="user-column-last-login"
                    :aria-sort="ariaSort('last_login_at')"
                  >
                    <button type="button" class="sort-button" @click="changeSort('last_login_at')">
                      Dernière connexion
                      <i :class="sortIcon('last_login_at')" aria-hidden="true"></i>
                    </button>
                  </th>
                  <th scope="col" class="text-right">Actions</th>
                </tr>
              </thead>

              <tbody v-if="isLoading" aria-busy="true" aria-label="Chargement des comptes">
                <tr v-for="index in skeletonRows" :key="`skeleton-${index}`" class="skeleton-row">
                  <td>
                    <div class="user-identity" aria-hidden="true">
                      <span class="skeleton skeleton--avatar"></span>
                      <span class="skeleton skeleton--name"></span>
                    </div>
                  </td>
                  <td class="user-column-email"><span class="skeleton skeleton--email"></span></td>
                  <td class="user-column-role"><span class="skeleton skeleton--badge"></span></td>
                  <td class="user-column-spinner"><span class="skeleton skeleton--text"></span></td>
                  <td><span class="skeleton skeleton--badge"></span></td>
                  <td class="user-column-last-login">
                    <div class="skeleton-last-login" aria-hidden="true">
                      <span class="skeleton skeleton--date"></span>
                      <span class="skeleton skeleton--date-caption"></span>
                    </div>
                  </td>
                  <td>
                    <div class="skeleton-actions" aria-hidden="true">
                      <span v-for="action in 4" :key="action" class="skeleton skeleton--action"></span>
                    </div>
                  </td>
                </tr>
              </tbody>

              <tbody v-else>
                <tr
                  v-for="user in users"
                  :key="user.id"
                  :class="{ 'user-row--deleted': Boolean(user.deleted_at) }"
                >
                  <td>
                    <div class="user-identity">
                      <span class="user-initials" aria-hidden="true">{{ initials(user.name) }}</span>
                      <div class="user-name">
                        <span>{{ user.name }}</span>
                        <span v-if="user.is_current" class="badge badge-you">Vous</span>
                      </div>
                    </div>
                  </td>
                  <td class="user-column-email">
                    <a :href="`mailto:${user.email}`" class="user-email">{{ user.email }}</a>
                  </td>
                  <td class="user-column-role">
                    <span class="badge role-badge" :class="roleBadgeClass(user.role)">
                      {{ user.role || 'Utilisateur' }}
                    </span>
                  </td>
                  <td class="user-column-spinner">{{ user.spinner_label }}</td>
                  <td>
                    <span class="badge status-badge" :class="statusBadgeClass(user)">
                      {{ statusLabel(user) }}
                    </span>
                    <span v-if="isInvitationPending(user)" class="invitation-pending">
                      Invitation en attente
                    </span>
                  </td>
                  <td class="user-column-last-login">
                    <div v-if="user.is_active && !user.deleted_at" class="last-login" :class="{ 'last-login--stale': isStale(user) }">
                      <span>{{ formatDate(user.last_login_at) }}</span>
                      <small>{{ inactivityLabel(user) }}</small>
                    </div>
                    <span v-else aria-label="Non applicable">—</span>
                  </td>
                  <td class="text-right">
                    <div v-if="!user.deleted_at" class="user-actions">
                      <button
                        type="button"
                        class="btn-action"
                        title="Modifier le compte"
                        :aria-label="`Modifier le compte de ${user.name}`"
                        :disabled="rowBusy(user.id)"
                        @click="editUser(user)"
                      >
                        <i class="fas fa-pen" aria-hidden="true"></i>
                      </button>

                      <button
                        v-if="user.is_active && !user.is_current"
                        type="button"
                        class="btn-action btn-action--warning"
                        title="Désactiver le compte"
                        :aria-label="`Désactiver le compte de ${user.name}`"
                        :disabled="rowBusy(user.id)"
                        @click="openConfirmation('deactivate', user)"
                      >
                        <i class="fas fa-user-slash" aria-hidden="true"></i>
                      </button>

                      <button
                        v-if="!user.is_active && !user.is_current"
                        type="button"
                        class="btn-action btn-action--success"
                        title="Réactiver le compte"
                        :aria-label="`Réactiver le compte de ${user.name}`"
                        :disabled="rowBusy(user.id)"
                        @click="runAction('activate', user)"
                      >
                        <i
                          :class="isActionLoading(user.id, 'activate') ? 'fas fa-spinner fa-spin' : 'fas fa-user-check'"
                          aria-hidden="true"
                        ></i>
                      </button>

                      <button
                        v-if="isInvitationPending(user)"
                        type="button"
                        class="btn-action"
                        title="Renvoyer l'invitation"
                        :aria-label="`Renvoyer l'invitation à ${user.name}`"
                        :disabled="rowBusy(user.id)"
                        @click="runAction('invite', user)"
                      >
                        <i
                          :class="isActionLoading(user.id, 'invite') ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"
                          aria-hidden="true"
                        ></i>
                      </button>

                      <button
                        v-if="user.is_active && !isInvitationPending(user)"
                        type="button"
                        class="btn-action"
                        title="Envoyer un lien de réinitialisation du mot de passe"
                        :aria-label="`Envoyer un lien de réinitialisation du mot de passe à ${user.name}`"
                        :disabled="rowBusy(user.id)"
                        @click="runAction('password-reset', user)"
                      >
                        <i
                          :class="isActionLoading(user.id, 'password-reset') ? 'fas fa-spinner fa-spin' : 'fas fa-key'"
                          aria-hidden="true"
                        ></i>
                      </button>

                      <button
                        v-if="!user.is_current"
                        type="button"
                        class="btn-action btn-action--danger"
                        title="Supprimer définitivement le compte"
                        :aria-label="`Supprimer définitivement le compte de ${user.name}`"
                        :disabled="rowBusy(user.id)"
                        @click="openConfirmation('delete', user)"
                      >
                        <i class="fas fa-trash-alt" aria-hidden="true"></i>
                      </button>
                    </div>
                    <span v-else class="deleted-actions-label">Consultation uniquement</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="isLoading"
            class="user-pagination-bar user-pagination-bar--skeleton"
            aria-hidden="true"
          >
            <span class="skeleton skeleton--page-size"></span>
            <div class="skeleton-pagination-buttons">
              <span v-for="button in 5" :key="button" class="skeleton skeleton--page-button"></span>
            </div>
            <span class="skeleton skeleton--summary"></span>
          </div>

          <div
            v-else-if="!loadError && users.length > 0"
            class="user-pagination-bar"
          >
            <div class="user-page-size">
              <el-select
                id="user-per-page"
                v-model="perPage"
                class="user-page-size-select"
                aria-label="Nombre de comptes par page"
                popper-class="user-filter-popper"
                @change="changePerPage"
              >
                <el-option :value="10" label="10"></el-option>
                <el-option :value="25" label="25"></el-option>
                <el-option :value="50" label="50"></el-option>
              </el-select>
            </div>

            <div class="user-pagination-center">
              <nav v-if="meta.last_page > 1" aria-label="Pagination des comptes">
                <ul class="user-pagination mb-0">
                  <li>
                    <button
                      type="button"
                      class="page-button page-button--nav"
                      :disabled="meta.current_page <= 1"
                      aria-label="Page précédente"
                      @click="goToPage(meta.current_page - 1)"
                    >
                      <i class="fas fa-chevron-left" aria-hidden="true"></i>
                    </button>
                  </li>
                  <li v-for="(item, index) in paginationItems" :key="`page-${item}-${index}`">
                    <span v-if="item === 'ellipsis'" class="page-ellipsis" aria-hidden="true">…</span>
                    <button
                      v-else
                      type="button"
                      class="page-button"
                      :class="{ 'page-button--active': item === meta.current_page }"
                      :aria-current="item === meta.current_page ? 'page' : null"
                      :aria-label="`Page ${item}`"
                      @click="goToPage(item)"
                    >
                      {{ item }}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      class="page-button page-button--nav"
                      :disabled="meta.current_page >= meta.last_page"
                      aria-label="Page suivante"
                      @click="goToPage(meta.current_page + 1)"
                    >
                      <i class="fas fa-chevron-right" aria-hidden="true"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>

            <p class="user-pagination-summary mb-0" aria-live="polite">
              {{ resultRange }} sur {{ meta.total }} compte{{ meta.total === 1 ? '' : 's' }}
            </p>
          </div>
        </div>
      </card>
    </div>

    <modal
      :show.sync="confirmation.visible"
      :show-close="!modalBusy"
      modal-classes="user-confirmation-modal"
      header-classes="align-items-center"
      aria-labelledby="user-confirmation-title"
      @close="closeConfirmation"
    >
      <h4 id="user-confirmation-title" slot="header" class="modal-title">
        {{ confirmationTitle }}
      </h4>

      <template v-if="confirmation.user">
        <div v-if="confirmation.type === 'deactivate'">
          <p>
            Le compte de <strong>{{ confirmation.user.name }}</strong> ne pourra plus se connecter.
            Cette action est réversible.
          </p>
        </div>

        <div v-else-if="confirmation.type === 'delete'">
          <p>
            Vous êtes sur le point de supprimer définitivement le compte de
            <strong>{{ confirmation.user.name }}</strong>.
          </p>
          <div class="delete-warning" role="alert">
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
            Cette action ne pourra pas être annulée.
          </div>
          <label for="delete-user-confirmation" class="delete-confirmation-label">
            Saisissez exactement <strong>{{ confirmation.user.name }}</strong> pour confirmer.
          </label>
          <input
            id="delete-user-confirmation"
            ref="deleteConfirmationInput"
            v-model="confirmation.input"
            type="text"
            autofocus
            class="form-control"
            autocomplete="off"
            :disabled="modalBusy"
            @keyup.enter="deleteNameMatches && confirmCurrentAction()"
          />
        </div>
      </template>

      <template slot="footer">
        <button
          type="button"
          class="btn user-modal-action user-modal-action--neutral"
          :disabled="modalBusy"
          @click="closeConfirmation"
        >
          Annuler
        </button>
        <button
          v-if="confirmation.type === 'delete' && confirmation.user && confirmation.user.is_active"
          type="button"
          class="btn user-modal-action user-modal-action--warning"
          :disabled="modalBusy"
          @click="runAction('deactivate', confirmation.user, true)"
        >
          <i
            :class="isActionLoading(confirmation.user.id, 'deactivate') ? 'fas fa-spinner fa-spin' : 'fas fa-user-slash'"
            class="mr-2"
            aria-hidden="true"
          ></i>
          Désactiver à la place
        </button>
        <button
          type="button"
          class="btn user-modal-action"
          :class="confirmation.type === 'delete' ? 'user-modal-action--danger' : 'user-modal-action--warning'"
          :disabled="modalBusy || (confirmation.type === 'delete' && !deleteNameMatches)"
          @click="confirmCurrentAction"
        >
          <i :class="modalPrimaryActionIcon" class="mr-2" aria-hidden="true"></i>
          {{ confirmation.type === 'delete' ? 'Supprimer définitivement' : 'Désactiver le compte' }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Select, Option } from 'element-ui'
import Modal from '@/components/Modal'
import { DEFAULT_SPINNER, getSpinnerLabel } from '@/components/Spinner/spinnerOptions'

const EMPTY_META = Object.freeze({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
  from: 0,
  to: 0
})

export default {
  name: 'UserList',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    Modal
  },
  data() {
    return {
      users: [],
      roles: [],
      search: '',
      role: '',
      status: '',
      sort: 'name',
      direction: 'asc',
      page: 1,
      perPage: 10,
      meta: { ...EMPTY_META },
      isLoading: true,
      isExporting: false,
      loadError: '',
      actionLoading: {},
      confirmation: {
        visible: false,
        type: '',
        user: null,
        input: ''
      },
      searchTimer: null,
      requestSequence: 0
    }
  },
  computed: {
    hasFilters() {
      return Boolean(this.search.trim() || this.role || this.status)
    },
    skeletonRows() {
      return Number(this.perPage) || 10
    },
    resultRange() {
      const from = Number(this.meta.from) || 0
      const to = Number(this.meta.to) || 0
      return from === to ? `${from}` : `${from}–${to}`
    },
    paginationItems() {
      const current = Number(this.meta.current_page) || 1
      const last = Number(this.meta.last_page) || 1

      if (last <= 7) {
        return Array.from({ length: last }, (value, index) => index + 1)
      }

      const pages = [1]
      const start = Math.max(2, current - 1)
      const end = Math.min(last - 1, current + 1)

      if (start > 2) pages.push('ellipsis')
      for (let page = start; page <= end; page += 1) pages.push(page)
      if (end < last - 1) pages.push('ellipsis')
      pages.push(last)
      return pages
    },
    deleteNameMatches() {
      return Boolean(
        this.confirmation.user &&
        this.confirmation.input === this.confirmation.user.name
      )
    },
    confirmationTitle() {
      return this.confirmation.type === 'delete'
        ? 'Supprimer définitivement le compte'
        : 'Désactiver le compte'
    },
    modalBusy() {
      if (!this.confirmation.user) return false
      return this.rowBusy(this.confirmation.user.id)
    },
    modalPrimaryActionIcon() {
      if (!this.confirmation.user) return ''
      if (this.isActionLoading(this.confirmation.user.id, this.confirmation.type)) {
        return 'fas fa-spinner fa-spin'
      }
      return this.confirmation.type === 'delete' ? 'fas fa-trash-alt' : 'fas fa-user-slash'
    }
  },
  mounted() {
    this.loadRoles()
    this.loadUsers()
  },
  beforeDestroy() {
    if (this.searchTimer) window.clearTimeout(this.searchTimer)
    this.requestSequence += 1
  },
  methods: {
    apiUrl(path) {
      return `${process.env.VUE_APP_API_BASE_URL || ''}${path}`
    },
    async loadRoles() {
      try {
        const response = await this.axios.get(this.apiUrl('role/list'))
        const payload = response.data || []
        const roleData = Array.isArray(payload) ? payload : payload.data || []

        this.roles = roleData
          .map(item => (typeof item === 'string' ? item : item.name))
          .filter(Boolean)
          .filter((item, index, values) => values.indexOf(item) === index)
          .sort((left, right) => left.localeCompare(right, 'fr', { sensitivity: 'base' }))
      } catch (error) {
        // The list remains usable: roles returned with users are merged below.
      }
    },
    async loadUsers() {
      const requestId = ++this.requestSequence
      this.isLoading = true
      this.loadError = ''

      try {
        const response = await this.axios.get(this.apiUrl('users'), {
          params: this.listParams()
        })

        if (requestId !== this.requestSequence) return

        const payload = response.data || {}
        const rawUsers = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.data)
            ? payload.data
            : []
        const rawMeta = Array.isArray(payload) ? {} : payload.meta || {}

        this.users = rawUsers.map(user => this.normalizeUser(user))
        this.mergeRolesFromUsers()
        this.meta = this.normalizeMeta(rawMeta, this.users.length)
        this.page = this.meta.current_page

        if (this.users.length === 0 && this.page > this.meta.last_page) {
          this.page = Math.max(this.meta.last_page, 1)
          await this.loadUsers()
        }
      } catch (error) {
        if (requestId !== this.requestSequence) return

        this.users = []
        this.meta = { ...EMPTY_META, per_page: this.perPage }
        this.loadError = this.errorMessage(
          error,
          'Une erreur est survenue pendant le chargement. Veuillez réessayer.'
        )
      } finally {
        if (requestId === this.requestSequence) this.isLoading = false
      }
    },
    listParams(includePagination = true) {
      const params = {
        sort: this.sort,
        direction: this.direction
      }

      if (this.search.trim()) params.search = this.search.trim()
      if (this.role) params.role = this.role
      if (this.status) params.status = this.status
      if (includePagination) {
        params.page = this.page
        params.per_page = this.perPage
      }
      return params
    },
    normalizeUser(user) {
      const rawRole = user.role
      let roleName = ''

      if (typeof rawRole === 'string') roleName = rawRole
      else if (rawRole && rawRole.name) roleName = rawRole.name
      else if (Array.isArray(rawRole) && rawRole.length) {
        roleName = typeof rawRole[0] === 'string' ? rawRole[0] : rawRole[0].name
      }

      const deleted = Boolean(user.deleted_at)
      const isCurrentFromWindow = Boolean(
        window.App && Number(window.App.id) === Number(user.id)
      )

      return {
        ...user,
        role: roleName || 'Utilisateur',
        spinner: user.spinner || DEFAULT_SPINNER,
        spinner_label: getSpinnerLabel(user.spinner || DEFAULT_SPINNER),
        is_active: user.is_active === undefined || user.is_active === null
          ? !deleted
          : this.toBoolean(user.is_active),
        is_current: this.toBoolean(user.is_current) || isCurrentFromWindow
      }
    },
    normalizeMeta(rawMeta, resultCount) {
      const currentPage = Number(rawMeta.current_page) || this.page
      const perPage = Number(rawMeta.per_page) || this.perPage
      const total = rawMeta.total === undefined ? resultCount : Number(rawMeta.total) || 0
      const lastPage = Number(rawMeta.last_page) || Math.max(Math.ceil(total / perPage), 1)
      const fallbackFrom = resultCount ? ((currentPage - 1) * perPage) + 1 : 0
      const fallbackTo = resultCount ? fallbackFrom + resultCount - 1 : 0

      return {
        current_page: currentPage,
        last_page: lastPage,
        per_page: perPage,
        total,
        from: rawMeta.from === null || rawMeta.from === undefined
          ? fallbackFrom
          : Number(rawMeta.from),
        to: rawMeta.to === null || rawMeta.to === undefined
          ? fallbackTo
          : Number(rawMeta.to)
      }
    },
    toBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },
    mergeRolesFromUsers() {
      const mergedRoles = this.roles.concat(this.users.map(user => user.role)).filter(Boolean)
      this.roles = mergedRoles
        .filter((item, index, values) => values.indexOf(item) === index)
        .sort((left, right) => left.localeCompare(right, 'fr', { sensitivity: 'base' }))
    },
    onSearchInput() {
      if (this.searchTimer) window.clearTimeout(this.searchTimer)
      this.searchTimer = window.setTimeout(() => {
        this.page = 1
        this.loadUsers()
      }, 350)
    },
    applyFilters() {
      if (this.searchTimer) window.clearTimeout(this.searchTimer)
      this.page = 1
      this.loadUsers()
    },
    clearFilters() {
      if (this.searchTimer) window.clearTimeout(this.searchTimer)
      this.search = ''
      this.role = ''
      this.status = ''
      this.page = 1
      this.loadUsers()
    },
    changePerPage() {
      this.page = 1
      this.loadUsers()
    },
    changeSort(column) {
      if (this.sort === column) {
        this.direction = this.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort = column
        this.direction = 'asc'
      }

      this.page = 1
      this.loadUsers()
    },
    sortIcon(column) {
      if (this.sort !== column) return 'fas fa-sort sort-icon sort-icon--inactive'
      return this.direction === 'asc'
        ? 'fas fa-sort-up sort-icon'
        : 'fas fa-sort-down sort-icon'
    },
    ariaSort(column) {
      if (this.sort !== column) return 'none'
      return this.direction === 'asc' ? 'ascending' : 'descending'
    },
    goToPage(page) {
      const targetPage = Number(page)
      if (
        targetPage < 1 ||
        targetPage > this.meta.last_page ||
        targetPage === this.meta.current_page
      ) return

      this.page = targetPage
      this.loadUsers()
    },
    addUser() {
      this.$router.push({ name: 'User Create' })
    },
    editUser(user) {
      this.$router.push({ name: 'User Edit', params: { id: user.id } })
    },
    initials(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)

      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
    },
    roleBadgeClass(role) {
      const normalizedRole = String(role || '').toLocaleLowerCase('fr')

      if (normalizedRole === 'admin') return 'role-badge--admin'
      if (normalizedRole === 'créateur' || normalizedRole === 'createur') return 'role-badge--creator'
      if (normalizedRole === 'commercial') return 'role-badge--commercial'
      if (normalizedRole === 'manager') return 'role-badge--manager'
      return 'role-badge--user'
    },
    statusLabel(user) {
      if (user.deleted_at) return 'Supprimé'
      return user.is_active ? 'Actif' : 'Désactivé'
    },
    statusBadgeClass(user) {
      if (user.deleted_at) return 'status-badge--deleted'
      return user.is_active ? 'status-badge--active' : 'status-badge--inactive'
    },
    isInvitationPending(user) {
      return Boolean(
        user.is_active &&
        user.invited_at &&
        !user.invitation_accepted_at &&
        !user.deleted_at
      )
    },
    parseDate(value) {
      if (!value) return null
      const date = value instanceof Date
        ? value
        : new Date(typeof value === 'string' ? value.replace(' ', 'T') : value)
      return Number.isNaN(date.getTime()) ? null : date
    },
    formatDate(value) {
      const date = this.parseDate(value)
      if (!date) return 'Jamais'

      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    inactivityDays(user) {
      if (user.inactive_days !== null && user.inactive_days !== undefined) {
        const serverDays = Number(user.inactive_days)
        if (Number.isFinite(serverDays)) return Math.max(serverDays, 0)
      }

      const date = this.parseDate(user.last_login_at)
      if (!date) return null

      const elapsed = Date.now() - date.getTime()
      return Math.max(Math.floor(elapsed / 86400000), 0)
    },
    inactivityLabel(user) {
      if (!user.is_active || user.deleted_at) return ''

      const days = this.inactivityDays(user)
      if (days === null) return 'Aucune connexion'
      if (days === 0) return "Aujourd'hui"
      if (days === 1) return 'Il y a 1 jour'
      return `Il y a ${days} jours`
    },
    isStale(user) {
      if (Object.prototype.hasOwnProperty.call(user, 'inactive_over_15_days')) {
        return Boolean(
          user.is_active &&
          !user.deleted_at &&
          this.toBoolean(user.inactive_over_15_days)
        )
      }

      const days = this.inactivityDays(user)
      return Boolean(user.is_active && !user.deleted_at && days !== null && days > 15)
    },
    openConfirmation(type, user) {
      if (user.is_current) {
        this.notify('Vous ne pouvez pas désactiver ou supprimer votre propre compte.', 'danger')
        return
      }

      this.confirmation = {
        visible: true,
        type,
        user,
        input: ''
      }

      if (type === 'delete') {
        this.$nextTick(() => {
          if (this.$refs.deleteConfirmationInput) this.$refs.deleteConfirmationInput.focus()
        })
      }
    },
    closeConfirmation() {
      if (this.modalBusy) return

      this.confirmation = {
        visible: false,
        type: '',
        user: null,
        input: ''
      }
    },
    confirmCurrentAction() {
      if (!this.confirmation.user || this.modalBusy) return
      if (this.confirmation.type === 'delete' && !this.deleteNameMatches) return
      this.runAction(this.confirmation.type, this.confirmation.user, true)
    },
    actionKey(userId, action) {
      return `${userId}:${action}`
    },
    isActionLoading(userId, action) {
      return Boolean(this.actionLoading[this.actionKey(userId, action)])
    },
    rowBusy(userId) {
      return Object.keys(this.actionLoading).some(key => key.indexOf(`${userId}:`) === 0)
    },
    async runAction(action, user, fromModal = false) {
      if (!user || this.rowBusy(user.id)) return

      const endpointByAction = {
        activate: `users/${user.id}/activate`,
        deactivate: `users/${user.id}/deactivate`,
        invite: `users/${user.id}/invite`,
        'password-reset': `users/${user.id}/password-reset`,
        delete: `users/${user.id}`
      }
      const successMessageByAction = {
        activate: 'Le compte a été réactivé.',
        deactivate: 'Le compte a été désactivé.',
        invite: "L'invitation a été envoyée.",
        'password-reset': 'Le lien de réinitialisation a été envoyé.',
        delete: 'Le compte a été supprimé.'
      }
      const endpoint = endpointByAction[action]
      if (!endpoint) return

      const key = this.actionKey(user.id, action)
      this.$set(this.actionLoading, key, true)

      try {
        if (action === 'delete') {
          await this.axios.delete(this.apiUrl(endpoint))
        } else {
          const requiresRedirectUrl = action === 'invite' || action === 'password-reset'
          const body = requiresRedirectUrl
            ? { redirect_url: `${window.location.origin}/password/email` }
            : {}

          await this.axios.post(this.apiUrl(endpoint), body)
        }

        this.notify(successMessageByAction[action], 'success')
        if (fromModal) {
          this.confirmation.visible = false
          this.confirmation.type = ''
          this.confirmation.user = null
          this.confirmation.input = ''
        }
        await this.loadUsers()
      } catch (error) {
        this.notify(
          this.errorMessage(error, "L'action n'a pas pu être effectuée."),
          'danger'
        )
      } finally {
        this.$delete(this.actionLoading, key)
      }
    },
    async exportUsers() {
      if (this.isExporting) return
      this.isExporting = true

      try {
        const response = await this.axios.get(this.apiUrl('users/export'), {
          params: { ...this.listParams(false), format: 'xlsx' },
          responseType: 'blob'
        })
        const contentDisposition = response.headers && response.headers['content-disposition']
        const filenameMatch = contentDisposition && contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
        const filename = filenameMatch
          ? decodeURIComponent(filenameMatch[1])
          : `comptes-utilisateurs-${new Date().toISOString().slice(0, 10)}.xlsx`
        const downloadUrl = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')

        link.href = downloadUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      } catch (error) {
        this.notify(
          this.errorMessage(error, "L'export Excel n'a pas pu être généré."),
          'danger'
        )
      } finally {
        this.isExporting = false
      }
    },
    errorMessage(error, fallback) {
      const responseData = error && error.response && error.response.data
      if (!responseData) return fallback
      if (typeof responseData === 'string') return responseData
      if (responseData.message) return responseData.message

      if (Array.isArray(responseData.errors)) {
        const jsonApiError = responseData.errors.find(Boolean)
        if (typeof jsonApiError === 'string') return jsonApiError
        if (jsonApiError && jsonApiError.detail) return jsonApiError.detail
        if (jsonApiError && jsonApiError.title) return jsonApiError.title
      } else if (responseData.errors) {
        const firstError = Object.keys(responseData.errors)
          .map(key => responseData.errors[key])
          .reduce((messages, value) => messages.concat(value), [])
          .find(Boolean)
        if (typeof firstError === 'string') return firstError
        if (firstError && firstError.detail) return firstError.detail
      }
      return fallback
    },
    notify(message, type) {
      this.$notify({
        message,
        type,
        verticalAlign: 'bottom'
      })
    }
  }
}
</script>

<style scoped>
.user-list-header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  width: 100%;
}

.user-list-heading {
  align-items: center;
  display: flex;
  gap: 10px;
  min-height: 40px;
}

.user-list-heading .card-title {
  line-height: 26px;
  margin: 0 !important;
}

.user-list-header-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.user-count {
  align-self: center;
  align-items: center;
  background: #f0f1f5;
  border-radius: 999px;
  color: #525f7f;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  height: 26px;
  justify-content: center;
  line-height: 1;
  min-height: 26px;
  min-width: 26px;
  padding: 0 10px;
  flex: 0 0 auto;
}

.user-list-content {
  padding: 4px 24px 24px;
}

.user-filters {
  align-items: flex-end;
  background: #f8f9fb;
  border: 1px solid #eceef3;
  border-radius: 10px;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(260px, 2fr) repeat(2, minmax(170px, 1fr));
  margin-bottom: 20px;
  padding: 16px;
}

.user-filters--with-actions {
  grid-template-columns: minmax(260px, 2fr) repeat(2, minmax(170px, 1fr)) auto;
}

.user-filter label {
  color: #525f7f;
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
}

.user-filter {
  min-width: 0;
}

.user-filter .form-control {
  background-color: #fff;
  border: 1px solid #dfe2e8;
  color: #2c2c2c;
  height: 40px;
}

.user-filter-select {
  display: block;
  height: 40px;
  width: 100%;
}

.user-filter-select >>> .el-input,
.user-filter-select >>> .el-input__inner {
  height: 40px;
}

.user-filter-select >>> .el-input__inner {
  background: #fff !important;
  border: 1px solid #d8dde5;
  border-radius: 8px;
  box-shadow: none !important;
  color: #2c2c2c !important;
  font-size: 12px;
  line-height: 40px;
  margin: 0 !important;
  padding-left: 12px;
}

.user-filter-select >>> .el-input__inner::placeholder {
  color: #98a2b3 !important;
  opacity: 1;
}

.user-filter-select >>> .el-input__inner:focus,
.user-filter-select >>> .el-input.is-focus .el-input__inner {
  background: #fff !important;
  border-color: #f96332;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .16) !important;
  color: #2c2c2c !important;
}

.user-filter-select:hover >>> .el-input__inner {
  border-color: #f96332;
}

.user-filter-select >>> .el-input__icon {
  color: #f96332 !important;
  line-height: 40px;
}

.user-filter .form-control:focus,
.delete-confirmation-label + .form-control:focus {
  border-color: #f96332;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .16);
}

.user-search-input {
  position: relative;
}

.user-search-input > i {
  color: #8b92a5;
  left: 14px;
  pointer-events: none;
  position: absolute;
  top: 13px;
}

.user-search-input .form-control {
  padding-left: 39px;
}

.user-filter-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  min-height: 40px;
}

.user-primary-action,
.user-secondary-action,
.user-export-action,
.user-modal-action {
  align-items: center;
  border-radius: 8px;
  box-shadow: none !important;
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  margin: 0 !important;
  padding: 0 14px;
  text-transform: none !important;
  transition: background-color .16s ease, border-color .16s ease, color .16s ease;
}

.user-export-action {
  background: #fff;
  border: 1px solid #cfd8d3;
  color: #34483d;
  gap: 8px;
  padding: 0 10px 0 8px;
}

.user-export-action:hover:not(:disabled),
.user-export-action:focus:not(:disabled) {
  background: #f2fbf6;
  border-color: #7fc39e;
  color: #11663b;
}

.user-export-action-icon {
  align-items: center;
  background: #e7f7ee;
  border-radius: 6px;
  color: #168453;
  display: inline-flex;
  flex: 0 0 26px;
  font-size: 13px;
  height: 26px;
  justify-content: center;
  width: 26px;
}

.user-export-format {
  align-items: center;
  background: #eef1f0;
  border-radius: 999px;
  color: #526078;
  display: inline-flex;
  font-size: 9px;
  font-weight: 800;
  height: 20px;
  justify-content: center;
  letter-spacing: .04em;
  min-width: 34px;
  padding: 0 7px;
}

.user-export-action:hover:not(:disabled) .user-export-format,
.user-export-action:focus:not(:disabled) .user-export-format {
  background: #dff3e7;
  color: #11663b;
}

.user-primary-action {
  background: #f96332;
  border: 1px solid #f96332;
  color: #fff;
}

.user-primary-action:hover:not(:disabled),
.user-primary-action:focus:not(:disabled) {
  background: #e95420;
  border-color: #e95420;
  color: #fff;
}

.user-secondary-action {
  background: #fff;
  border: 1px solid #d8dde5;
  color: #526078;
}

.user-secondary-action:hover:not(:disabled),
.user-secondary-action:focus:not(:disabled) {
  background: #fff7f4;
  border-color: #f8b49e;
  color: #d94c1e;
}

.user-primary-action:disabled,
.user-secondary-action:disabled,
.user-export-action:disabled,
.user-modal-action:disabled {
  box-shadow: none !important;
  cursor: not-allowed;
  opacity: .55;
}

.user-table-wrapper {
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  overflow: hidden;
}

.user-table {
  color: #344054;
  min-width: 1050px;
  table-layout: fixed;
}

.user-table col:nth-child(1) { width: 19.5%; }
.user-table col:nth-child(2) { width: 16.3%; }
.user-table col:nth-child(3) { width: 11.3%; }
.user-table col:nth-child(4) { width: 11.9%; }
.user-table col:nth-child(5) { width: 8.3%; }
.user-table col:nth-child(6) { width: 16.1%; }
.user-table col:nth-child(7) { width: 16.6%; }

.user-table thead th {
  background: #f8f9fb;
  border-bottom: 1px solid #e8eaf0;
  border-top: 0;
  color: #667085;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  padding: 12px 14px;
  text-transform: uppercase;
  vertical-align: middle;
}

.user-table tbody td {
  border-color: #eef0f4;
  font-size: 13px;
  padding: 13px 14px;
  vertical-align: middle;
}

.user-table tbody tr {
  height: 63px;
}

.user-table tbody tr:last-child td {
  border-bottom: 0;
}

.user-table tbody tr:hover {
  background: #fcfcfd;
}

.sort-button {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 7px;
  letter-spacing: inherit;
  padding: 3px 0;
  text-transform: inherit;
}

.sort-button:focus-visible,
.btn-action:focus-visible,
.page-button:focus-visible {
  border-radius: 4px;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .25);
  outline: 0;
}

.sort-icon {
  color: #f96332;
  font-size: 11px;
}

.sort-icon--inactive {
  color: #b8bdc9;
}

.user-identity {
  align-items: center;
  display: flex;
  gap: 10px;
}

.user-initials {
  align-items: center;
  background: #fff1eb;
  border: 1px solid #ffd9ca;
  border-radius: 50%;
  color: #d94c1e;
  display: inline-flex;
  flex: 0 0 36px;
  font-size: 12px;
  font-weight: 700;
  height: 36px;
  justify-content: center;
  letter-spacing: .02em;
  width: 36px;
}

.user-name {
  align-items: center;
  color: #202939;
  display: flex;
  font-weight: 600;
  gap: 7px;
  min-width: 0;
}

.user-name > span:first-child,
.user-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-email {
  color: #53627a;
  display: inline-block;
  max-width: 240px;
}

.badge-you {
  background: #e8f2ff;
  color: #2463a9;
  font-size: 9px;
  letter-spacing: .03em;
  padding: 4px 6px;
  text-transform: uppercase;
}

.role-badge,
.status-badge {
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .02em;
  padding: 6px 9px;
}

.role-badge--admin { background: #feecec; color: #b42318; }
.role-badge--creator { background: #f2eaff; color: #6f3dc0; }
.role-badge--commercial { background: #e9f8ef; color: #18794e; }
.role-badge--manager { background: #e9f2ff; color: #175cd3; }
.role-badge--user { background: #f0f1f5; color: #525f7f; }
.status-badge--active { background: #e8f7ee; color: #137a46; }
.status-badge--inactive { background: #fff3db; color: #946200; }
.status-badge--deleted { background: #f0f1f5; color: #667085; }

.invitation-pending {
  color: #946200;
  display: block;
  font-size: 10px;
  font-weight: 600;
  margin-top: 5px;
  white-space: nowrap;
}

.last-login {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
}

.last-login small {
  color: #7b8496;
  font-size: 10px;
  margin-top: 2px;
}

.last-login--stale,
.last-login--stale small {
  color: #c82a2a;
  font-weight: 700;
}

.user-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: flex-end;
}

.btn-action {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe2e8;
  border-radius: 7px;
  color: #53627a;
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  width: 32px;
}

.btn-action:hover:not(:disabled) {
  background: #fff4f0;
  border-color: #f7b49f;
  color: #d94c1e;
}

.btn-action:disabled { cursor: not-allowed; opacity: .5; }
.btn-action--success { color: #137a46; }
.btn-action--warning { color: #946200; }
.btn-action--danger { color: #c82a2a; }
.btn-action--success:hover:not(:disabled) { background: #effaf3; border-color: #a9dbbd; color: #137a46; }
.btn-action--warning:hover:not(:disabled) { background: #fff8e8; border-color: #ead092; color: #946200; }
.btn-action--danger:hover:not(:disabled) { background: #fff0f0; border-color: #efb0b0; color: #c82a2a; }
.user-row--deleted td { color: #8b92a5; }

.deleted-actions-label {
  color: #8b92a5;
  font-size: 11px;
  white-space: nowrap;
}

.user-state {
  align-items: center;
  border: 1px dashed #d8dce5;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  justify-content: center;
  padding: 36px 20px;
  text-align: center;
}

.user-state-icon {
  align-items: center;
  background: #f2f4f7;
  border-radius: 50%;
  color: #667085;
  display: flex;
  font-size: 20px;
  height: 52px;
  justify-content: center;
  margin-bottom: 14px;
  width: 52px;
}

.user-state h5 {
  color: #202939;
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 6px;
}

.user-state p {
  color: #667085;
  margin-bottom: 18px;
  max-width: 460px;
}

.user-state--error .user-state-icon { background: #fff0f0; color: #c82a2a; }

.skeleton {
  animation: user-list-pulse 1.4s ease-in-out infinite;
  background: #e9ebf0;
  border-radius: 5px;
  display: block;
  height: 13px;
}

.skeleton-row {
  height: 63px;
}

.skeleton--avatar {
  border-radius: 50%;
  flex: 0 0 36px;
  height: 36px;
  width: 36px;
}

.skeleton--name { width: 128px; }
.skeleton--email { max-width: 210px; width: 82%; }
.skeleton--badge { border-radius: 999px; height: 25px; width: 76px; }
.skeleton--text { width: 100px; }
.skeleton--date { width: 125px; }
.skeleton--date-caption { height: 10px; width: 88px; }

.skeleton-last-login {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.skeleton-actions {
  align-items: center;
  display: flex;
  gap: 5px;
  justify-content: flex-end;
}

.skeleton--action {
  border-radius: 7px;
  flex: 0 0 32px;
  height: 32px;
  width: 32px;
}

.user-pagination-bar--skeleton {
  border-top-color: transparent;
}

.skeleton--page-size {
  height: 34px;
  justify-self: start;
  width: 72px;
}

.skeleton-pagination-buttons {
  display: flex;
  gap: 5px;
  justify-self: center;
}

.skeleton--page-button {
  border-radius: 7px;
  height: 32px;
  width: 32px;
}

.skeleton--summary {
  justify-self: end;
  width: 118px;
}

@keyframes user-list-pulse {
  0%, 100% { opacity: .55; }
  50% { opacity: 1; }
}

.user-pagination-bar {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  min-height: 54px;
  padding-top: 16px;
}

.user-page-size {
  align-items: center;
  display: inline-flex;
  justify-self: start;
}

.user-page-size-select {
  width: 68px;
}

.user-page-size-select >>> .el-input,
.user-page-size-select >>> .el-input__inner {
  height: 34px;
}

.user-page-size-select >>> .el-input__inner {
  background: #fff !important;
  border: 1px solid #d8dde5;
  border-radius: 8px;
  box-shadow: none !important;
  color: #465266 !important;
  font-size: 11px;
  line-height: 34px;
  margin: 0 !important;
  padding: 0 27px 0 10px;
}

.user-page-size-select >>> .el-input__inner:focus,
.user-page-size-select >>> .el-input.is-focus .el-input__inner {
  background: #fff !important;
  border-color: #f96332;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .16) !important;
  color: #465266 !important;
}

.user-page-size-select:hover >>> .el-input__inner {
  border-color: #f96332;
}

.user-page-size-select >>> .el-input__icon {
  color: #f96332 !important;
  line-height: 34px;
}

.user-pagination-center {
  justify-self: center;
  min-width: 34px;
}

.user-pagination-summary {
  color: #667085;
  font-size: 11px;
  justify-self: end;
  text-align: right;
  white-space: nowrap;
}

.user-pagination {
  align-items: center;
  display: flex;
  gap: 5px;
  list-style: none;
  padding: 0;
}

.page-button {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe2e8;
  border-radius: 8px;
  color: #53627a;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  height: 34px;
  justify-content: center;
  min-width: 34px;
  padding: 0 8px;
}

.page-button:hover:not(:disabled) {
  background: #fff4ef;
  border-color: #f96332;
  color: #d94c1e;
}

.page-button--active { background: #f96332; border-color: #f96332; color: #fff; }
.page-button:disabled { cursor: not-allowed; opacity: .45; }

.page-ellipsis {
  color: #8b92a5;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

.delete-warning {
  align-items: center;
  background: #fff2f0;
  border: 1px solid #ffd2cc;
  border-radius: 8px;
  color: #a61b1b;
  display: flex;
  font-size: 13px;
  gap: 9px;
  margin: 14px 0 18px;
  padding: 11px 13px;
}

.delete-confirmation-label { color: #344054; font-size: 13px; }

.user-modal-action {
  height: 38px;
  padding: 0 13px;
}

.user-modal-action--neutral {
  background: #fff;
  border: 1px solid #d8dde5;
  color: #526078;
}

.user-modal-action--neutral:hover:not(:disabled) {
  background: #f8f9fb;
  border-color: #cbd1da;
  color: #344054;
}

.user-modal-action--warning {
  background: #fff8e8;
  border: 1px solid #ead092;
  color: #946200;
}

.user-modal-action--warning:hover:not(:disabled) {
  background: #fff3d6;
  border-color: #dfbd70;
  color: #805500;
}

.user-modal-action--danger {
  background: #c82a2a;
  border: 1px solid #c82a2a;
  color: #fff;
}

.user-modal-action--danger:hover:not(:disabled) {
  background: #a92121;
  border-color: #a92121;
  color: #fff;
}

@media (max-width: 1199px) {
  .user-filters {
    grid-template-columns: minmax(230px, 2fr) repeat(2, minmax(150px, 1fr));
  }

  .user-filters--with-actions { grid-template-columns: minmax(230px, 2fr) repeat(2, minmax(150px, 1fr)); }
  .user-filter-actions { grid-column: 1 / -1; }
}

@media (max-width: 767px) {
  .user-list-header { align-items: stretch; flex-direction: column; }
  .user-list-header-actions { align-self: stretch; }
  .user-list-header-actions .btn { flex: 1; }
  .user-list-content { padding: 4px 14px 18px; }
  .user-filters,
  .user-filters--with-actions { grid-template-columns: 1fr 1fr; padding: 13px; }
  .user-filter--search,
  .user-filter-actions { grid-column: 1 / -1; }
  .user-filter-actions { align-items: stretch; flex-direction: column; }
  .user-filter-actions .btn { width: 100%; }
  .user-table-wrapper { overflow: visible; }
  .user-table { min-width: 0; table-layout: fixed; width: 100%; }
  .user-column-email,
  .user-column-role,
  .user-column-spinner,
  .user-column-last-login { display: none; }
  .user-table th:first-child,
  .user-table td:first-child { width: 41%; }
  .user-table th:nth-child(5),
  .user-table td:nth-child(5) { width: 24%; }
  .user-table th:last-child,
  .user-table td:last-child { width: 35%; }
  .user-table col:first-child { width: 41%; }
  .user-table col:nth-child(5) { width: 24%; }
  .user-table col:last-child { width: 35%; }
  .user-table thead th,
  .user-table tbody td { padding: 10px 8px; }
  .user-initials { display: none; }
  .skeleton--avatar { display: none; }
  .skeleton--action { flex-basis: 30px; height: 30px; width: 30px; }
  .user-name { align-items: flex-start; flex-direction: column; gap: 4px; }
  .status-badge { font-size: 9px; padding: 5px 7px; }
  .invitation-pending { white-space: normal; }
  .btn-action { height: 30px; width: 30px; }
  .user-pagination-bar { grid-template-columns: 1fr; gap: 12px; }
  .user-page-size { justify-self: start; }
  .user-pagination-center { justify-self: center; }
  .user-pagination-summary { justify-self: end; }
  .user-pagination { flex-wrap: wrap; justify-content: center; }
}

@media (max-width: 420px) {
  .user-list-header-actions { align-items: stretch; flex-direction: column-reverse; }
  .user-filters,
  .user-filters--with-actions { grid-template-columns: 1fr; }
  .user-filter--search,
  .user-filter-actions { grid-column: auto; }
  .user-table th:first-child,
  .user-table td:first-child { width: 35%; }
  .user-table th:nth-child(5),
  .user-table td:nth-child(5) { width: 25%; }
  .user-table th:last-child,
  .user-table td:last-child { width: 40%; }
  .user-table col:first-child { width: 35%; }
  .user-table col:nth-child(5) { width: 25%; }
  .user-table col:last-child { width: 40%; }
}
</style>

<style>
.user-filter-popper {
  background: #fff !important;
  border: 1px solid #e1e5eb !important;
  border-radius: 8px !important;
  box-shadow: 0 12px 30px rgba(35, 48, 68, .16) !important;
  margin-top: 6px !important;
  z-index: 12050 !important;
}

.user-filter-popper .el-select-dropdown__item {
  color: #465266;
  font-size: 12px;
  height: 34px;
  line-height: 34px;
  padding: 0 12px;
}

.user-filter-popper .el-select-dropdown__item.hover,
.user-filter-popper .el-select-dropdown__item:hover {
  background: #fff4f0;
}

.user-filter-popper .el-select-dropdown__item.selected {
  color: #f96332;
  font-weight: 700;
}
</style>
